'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { JSX, useEffect, useRef } from 'react';
import { PullToRefreshIndicator } from '@/components/PullToRefreshIndicator';
import { PageHeader, PageHeaderTitle } from '@/components/page-header';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { getQueryConfig } from '@/lib/tanstackQuery/queryRegistry';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

import { OrdersList } from './orders-list';

const PULL_THRESHOLD = 80;

export function OrdersPage(): JSX.Element {
  const t = useTranslations('employeeOrders');

  const { data, isLoading, error, refetch } = useQuery({
    ...getQueryConfig('employee-orders-history'),
  });

  const { pullDistance, isRefreshing, containerRef } = usePullToRefresh(
    async () => {
      await refetch();
      trackingManager.trackEvent('EMPLOYEE_ORDERS_REFRESHED', {
        orderCount: data?.orders?.length ?? 0,
      });
    },
    { threshold: PULL_THRESHOLD },
  );

  const hasTrackedView = useRef(false);

  useEffect(() => {
    if (data && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackingManager.trackEvent('EMPLOYEE_ORDERS_VIEWED', {
        orderCount: data.orders?.length ?? 0,
        filter: 'upcoming',
      });
    }
  }, [data]);

  return (
    <div ref={containerRef} className="mx-auto w-full max-w-2xl space-y-6 max-xs:space-y-4">
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        threshold={PULL_THRESHOLD}
        isRefreshing={isRefreshing}
      />

      <PageHeader className="max-xs:gap-2">
        <PageHeaderTitle className="max-xs:text-xl">{t('title')}</PageHeaderTitle>
      </PageHeader>

      <OrdersList orders={data?.orders ?? []} isLoading={isLoading} error={error} />
    </div>
  );
}
